import { useRef } from 'react'
import { Upload } from 'lucide-react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'

interface CogoApplicationImportButtonProps {
  showLabel?: boolean
  className?: string
  buttonClassName?: string
  label?: string
}

export function CogoApplicationImportButton({
  showLabel = false,
  className,
  buttonClassName,
  label = 'Upload Coogo JSON',
}: CogoApplicationImportButtonProps) {
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const importCogoApplication = useStudioStore((s) => s.importCogoApplication)

  const handleImportCogoApplication = async (file: File | undefined) => {
    if (!file) return
    try {
      const text = await file.text()
      const parsed = JSON.parse(text) as unknown
      await importCogoApplication(parsed, file.name)
      toast.success('Coogo JSON imported')
    } catch (err) {
      console.error('[CogoApplicationImportButton] Coogo JSON import failed:', err)
      toast.error(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      if (importInputRef.current) importInputRef.current.value = ''
    }
  }

  return (
    <div className={className}>
      <input
        ref={importInputRef}
        type="file"
        accept=".json,.cogo.json,.cogo-app.json,application/json"
        className="hidden"
        aria-hidden
        tabIndex={-1}
        data-testid="cogo-application-import-input"
        onChange={(event) => { void handleImportCogoApplication(event.currentTarget.files?.[0]) }}
      />
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => importInputRef.current?.click()}
        className={
          buttonClassName ??
          'inline-flex h-10 items-center gap-1.5 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent'
        }
      >
        <Upload size={16} strokeWidth={1.7} />
        {showLabel && <span>{label}</span>}
      </button>
    </div>
  )
}
